import { useGetKeyPerson, useGetRoleEligibleEntities } from '@commenda/api';
import {
  Button,
  FullscreenModal,
  FullscreenModalContent,
  Loading,
  LoadingIndicator,
} from '@commenda/kakashi';
import { useRef, useState } from 'react';
import {
  IncorporationFormHeader,
  SupportCard,
} from '../../form-builder/components/new-incorp-layout';
import { AssignRoleStep, type AssignRoleStepHandle } from './AssignRoleStep';

/**
 * Single-step screen for adding role(s) to an existing key person at a fixed entity — no wizard,
 * since there's nothing to choose but the roles. Shares AssignRoleStep with the create wizard.
 */
export const AddRoleForm = ({
  isOpen,
  onClose,
  keyPersonId,
  corporationId,
  companyId,
}: {
  isOpen: boolean;
  onClose: () => void;
  keyPersonId: number;
  corporationId: number;
  companyId: number;
}) => {
  const roleStepRef = useRef<AssignRoleStepHandle>(null);
  const [roleStepState, setRoleStepState] = useState({
    canSubmit: false,
    isSubmitting: false,
  });

  const { data: keyPerson } = useGetKeyPerson(isOpen ? keyPersonId : undefined);
  const { data: eligibleEntities, isLoading } = useGetRoleEligibleEntities(
    keyPersonId,
    { enabled: isOpen && keyPersonId > 0 }
  );

  const entity = (eligibleEntities ?? []).find(
    (e) => e.corporationId === corporationId
  );
  const displayName =
    [keyPerson?.firstName, keyPerson?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim() || 'this person';

  return (
    <FullscreenModal isOpen={isOpen} onClose={onClose} enableEscapeKey>
      <IncorporationFormHeader onClose={onClose} />
      <FullscreenModalContent className="flex min-h-0 items-start justify-center overflow-y-auto bg-bg-light">
        <div className="flex w-full max-w-[1190px] items-start gap-7xl py-5xl pb-8xl">
          <div className="w-[280px] shrink-0 pt-9xl">
            <SupportCard />
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-5xl">
            <div className="flex flex-col gap-xs">
              <h1 className="display-xs-bold text-text-primary">
                Add role(s) for {displayName}
              </h1>
              <p className="txt-small-regular text-text-secondary">
                Define this individual&apos;s relationship to the entity.
              </p>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-4xl">
                <Loading />
              </div>
            ) : !entity ? (
              <p className="txt-small-regular text-text-tertiary">
                This entity isn&apos;t available for new role assignments.
              </p>
            ) : (
              <AssignRoleStep
                ref={roleStepRef}
                keyPersonId={keyPersonId}
                companyId={companyId}
                entities={[entity]}
                onAssigned={onClose}
                onStateChange={setRoleStepState}
              />
            )}

            <div className="flex items-center justify-end gap-xl">
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={() => roleStepRef.current?.submit()}
                disabled={!entity || !roleStepState.canSubmit}
              >
                {roleStepState.isSubmitting ? (
                  <LoadingIndicator size={16} />
                ) : null}
                <span>Add role(s)</span>
              </Button>
            </div>
          </div>
        </div>
      </FullscreenModalContent>
    </FullscreenModal>
  );
};
